/* @flow */

import { type Analytics, getAnalytics } from 'firebase/analytics';
import { type FirebaseApp, initializeApp } from 'firebase/app';
// $FlowFixMe[missing-export]
import { type Messaging, getMessaging, getToken, onMessage } from 'firebase/messaging';

import { firebaseConfig, firebaseVapidKey } from './helpers/applicationCustomization/ui';
import { logDebug, logError } from './helpers/debug/debug';
import { initializeFcmLifecycleManager } from './helpers/firebase/firebase';
import { isFirebaseConfigValid } from './helpers/firebase/helpers';
import { StorageKeys } from './helpers/localStorage/keys';
import LocalStorageManager from './helpers/localStorage/localStorageManager';
import { Messenger, MessengerEvents } from './helpers/messenger';

// Check if Firebas is enabled for current app
const firebase: { analytics: Analytics | null, app: FirebaseApp | null, enabled: boolean, messaging: Messaging | null } = {
  analytics: null,
  app: null,
  enabled: isFirebaseConfigValid(firebaseConfig),
  messaging: null,
};

const processMessagePayload = (data?: { action: string, type: string }) => {
  const { action, type } = data ?? {};

  if (type === 'dms' && action === 'request') {
    Messenger.emit(MessengerEvents.DMS_REQUEST, fcmRegistrationToken);
  }
};

if (firebase.enabled) {
  // Initialize Firebase
  firebase.app = initializeApp(firebaseConfig);

  // Initialize Firebase Analytics
  firebase.analytics = getAnalytics(firebase.app);

  if (firebase.app) {
    // Initialize Firebase Cloud Messaging (FCM)
    firebase.messaging = getMessaging(firebase.app);
  }

  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'FCM_NOTIFICATION_CLICKED') {
        logDebug('Message received from service worker');
        processMessagePayload(event.data.payload);
      }
    });
  }
}

// Store FCM registration token for future uses
let fcmRegistrationToken: string | null = null;

// Used to prevent race condition in ensureFcmToken
let tokenPromise: Promise<string | null> | null = null;

/*
 * Retrieve and store the FCM registration token
 * (handle permission + token + refresh detection)
 */
const ensureFcmToken = (): Promise<string | null> => {
  if (!firebase.enabled || firebase.messaging === null) {
    return Promise.resolve(null);
  }

  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      // 1. Ensure permission
      if (Notification.permission !== 'granted') {
        logDebug('Notification permission not granted, requesting...');
        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
          logDebug('Unable to get permission to notify');
          return null;
        }

        logDebug('Notification permission granted');
      }

      if (!firebase.messaging) {
        logDebug('firebase.messaging is null');
        return null;
      }

      // 2. Get token (also refresh it if needed)
      const currentToken = await getToken(firebase.messaging, {
        vapidKey: firebaseVapidKey || undefined,
      });

      if (!currentToken) {
        logDebug('No registration token available');
        return null;
      }

      const storedToken = LocalStorageManager.loadString(StorageKeys.FcmToken, '');

      // 3. 🔑 Detect refresh
      if (currentToken !== storedToken) {
        logDebug('FCM registration token updated (new or refreshed)');

        fcmRegistrationToken = currentToken;
        LocalStorageManager.save(StorageKeys.FcmToken, currentToken);

        // Send new token to backend through DMS request
        Messenger.emit(MessengerEvents.DMS_REQUEST, currentToken);

        logDebug(`FCM registration token: ${currentToken}`);
      } else {
        // Token unchanged → just reuse
        fcmRegistrationToken = currentToken;
        logDebug('FCM registration token unchanged');
      }

      return currentToken;
    } catch (error) {
      logError(`Error while retrieving FCM registration token: ${error.message}`);
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
};

/*
 * Retrieve FCM registration token
 */
const getFcmToken = async (): Promise<string | null> => {
  if (fcmRegistrationToken) {
    // Return cached token from module
    return fcmRegistrationToken;
  }

  const storedToken = LocalStorageManager.loadString(StorageKeys.FcmToken, '');

  if (storedToken !== '') {
    // Return cached token from local storage
    fcmRegistrationToken = storedToken;
    return storedToken;
  }

  // If not found in memory or localStorage, try to generate it (will request permission if needed)
  return await ensureFcmToken();
};

/*
 * Foreground messages handler
 */
if (firebase.messaging !== null) {
  onMessage(firebase.messaging, (payload) => {
    logDebug('Foreground message received');
    processMessagePayload(payload.data);
  });
}

/*
 * Initialization
 */
const initializeFirebase = () => {
  if (!firebase.enabled || firebase.messaging === null) {
    return;
  }

  initializeFcmLifecycleManager({ ensureFcmToken });
};

export { firebase, getFcmToken, initializeFirebase };
export default firebase.app;
